import { formatAmount, formatConfidence, formatDate } from '@shared/lib/format';
import { Modal } from '@shared/ui/modal/Modal';
import { ReceiptStatusBadge } from './ReceiptStatusBadge';
import { DOCUMENT_TYPE_LABELS } from '../types';
import type { Receipt } from '../types';

export interface ReceiptDetailsModalProps {
  receipt: Receipt | null;
  onClose: () => void;
}

/** Ligne de détail (libellé + valeur). */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-right text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

/** Affiche les détails complets d'un ticket dans une modale. */
export function ReceiptDetailsModal({ receipt, onClose }: ReceiptDetailsModalProps) {
  if (!receipt) return null;

  return (
    <Modal open={receipt !== null} onClose={onClose} title="Détails du ticket">
      <div className="flex flex-col gap-4">
        {/* Aperçu du fichier */}
        {receipt.previewUrl && receipt.fileType !== 'application/pdf' ? (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            <img
              src={receipt.previewUrl}
              alt={`Aperçu du document ${receipt.fileName}`}
              className="max-h-48 w-full object-contain"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <span className="text-xs font-medium text-slate-500">Fichier</span>
            <span className="truncate text-sm text-slate-700">{receipt.fileName}</span>
          </div>
        )}

        {/* Détails */}
        <dl className="divide-y divide-slate-100">
          <DetailRow label="Commerçant" value={receipt.merchant} />
          <DetailRow label="Date" value={formatDate(receipt.date)} />
          <DetailRow label="Montant TTC" value={formatAmount(receipt.total, receipt.currency)} />
          <DetailRow label="TVA" value={formatAmount(receipt.vat, receipt.currency)} />
          <DetailRow label="Devise" value={receipt.currency} />
          <DetailRow label="Catégorie" value={receipt.category} />
          <DetailRow label="Type" value={DOCUMENT_TYPE_LABELS[receipt.documentType]} />
          <DetailRow
            label="Confiance IA"
            value={
              <span
                className={
                  receipt.confidence >= 0.85
                    ? 'text-emerald-700'
                    : receipt.confidence >= 0.6
                      ? 'text-amber-700'
                      : 'text-red-700'
                }
              >
                {formatConfidence(receipt.confidence)}
              </span>
            }
          />
          <DetailRow label="Statut" value={<ReceiptStatusBadge status={receipt.status} />} />
          <DetailRow label="Ajouté le" value={formatDate(receipt.createdAt.slice(0, 10))} />
        </dl>
      </div>
    </Modal>
  );
}
